/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import React, { useEffect, useState } from "react";
import { useParams, useHistory } from 'react-router-dom';
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Badge,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
// node.js library that concatenates classes (strings)
import classnames from "classnames";

import Header from "../../../components/Headers/Header";
import OptionTabs from "../../../components/Tabs/OptionTabs";
import Image from "../../../components/utils/Image";

import { getStudentInformation, deleteProfileImage, getStudentProfile, getStudentAssessmentData } from '../../../Services/Student/StudentService';
import { NewUser, TypeLeaderboard, TypeMaterialProgress, TypeStudentAssessmentStats, TypeStudentProfile } from "../../../types";

import './studentProfile.scss';
import { getAppUsagesDataByStudent, getProgressForAllTopic, getStudentLeaderboardStats, getStudentOverallMaterialProgress } from "../../../Services/Stats/StatService";
import ActivityRing from "../../../components/ActivityRing/ActivityRing";
import Column from "../../../components/Charts/Column";
import { groupBy } from "../../../Services/utils";
import moment from "moment";
import AreaChart from "../../../components/Charts/AreaChart";
import PairedColumnChart from "../../../components/Charts/PairedColumnChart";

const StudentProfile = () => {
  var { id } = useParams(); // getting id from params path
  const history = useHistory();

  const [tab, setTab] = useState<number>(1); // which table to show for assessment/homework stat
  const [statTab, setStatTab] = useState<number>(1); // whether to show profile or stat
  const [activeNav, setActiveNav] = useState(1); // for line chart(student's app usage)

  const [studentInformation, setStudentInformation] = useState<NewUser>(); // stores student information
  const [studentProfile, setStudentProfile] = useState<TypeStudentProfile>(); // stores student profile details

  // holds stats for available assessments
  const [studentAssessmentStats, setStudentAssessmentStats] = useState<TypeStudentAssessmentStats>({
    total: 0,
    progress: 0,
    attempted: 0,
    unattempted: 0
  });
  // holds stats for available homeworks
  const [studentHomeworkStats, setStudentHomeworkStats] = useState<TypeStudentAssessmentStats>({
    total: 0,
    progress: 0,
    attempted: 0,
    unattempted: 0
  });

  const [editableUserInfo, setEditableUserInfo] = useState<NewUser>();
  const [isEditing, setIsEdition] = useState<Boolean>(false); // when user wants to edit information --- true, this enables the input fields when true

  // const [modal, setModal] = useState(false);
  // const toggle = () => setModal(!modal);

  const [selectedImg, setSelectedImg] = useState<string>(""); // image that is selected from local system to show on the page
  const [fileID, setFileID] = useState<number>(0); // stores previously uploaded image

  // *** STATS ***
  const [totalMaterialProgress, setTotalMaterialProgress] = useState<number>(); // holds progress average for materials
  const [studentMaterialProgress, setStudentMaterialProgress] = useState<TypeMaterialProgress>({
    labels: [],
    performance: [],
    progress: []
  }); // holds material & assessment progress
  const [leaderboardStats, setLeaderboardStats] = useState<TypeLeaderboard[]>(); // holds leaderboard data
  const [activityData, setActivityData] = useState<any>([]); // holds data for activity ring component's propertyes
  const [columnData, setColumnData] = useState<any>({
    chartData: [],
    chartLabels: [],
    colors: ['#F44336', '#E91E63', '#9C27B0']
  }); // holds data for column chart component's properties

  const [appUsage, setAppUsage] = useState(); // stores app usage data
  const [appUsageChartData, setAppUsageChartData] = useState<{ labels: string[], data: number[] }>(); // stores app usage data to create a line chart

  useEffect(() => {
    getStudentInfo();
    setFileID(studentInformation?.avatar);
  }, [id]);

  // fetches student information & profile data when page is visited
  const getStudentInfo = () => {
    getStudentInformation(id).then(
      (response: AxiosResponse) => {
        // console.log("Response for student: ", response?.data?.data);
        setStudentInformation(response?.data?.data);
        setEditableUserInfo(response?.data?.data);
        getStudentProfileInfo(response?.data?.data?.id); // fetches student profile
        getStudentAssessments(response?.data?.data?.id); // fetch student assessments info
        // getProgress(response?.data?.data?.id); // fetch student progress for all topic/chapter
        getStudentAppUsage(id); // fetch student's app usage
      }, (error) => {
        console.log("Error caught: ", error?.response);
        toast.error("Can not fetch user information!");
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };
  // fetches student-profile information
  const getStudentProfileInfo = (studentId: number) => {
    getStudentProfile(studentId).then(
      (response: AxiosResponse) => {
        // console.log("Response for student profile: ", response?.data?.data);
        setStudentProfile(response?.data?.data);
        let current_grade = response?.data?.data?.gradelist?.map(({ grade_section_id }) => {
          if (moment(new Date()).format('YYYY-MM-DD') >= moment(grade_section_id?.session?.session_start).format('YYYY-MM-DD')
            &&
            moment(new Date()).format('YYYY-MM-DD') <= moment(grade_section_id?.session?.session_end).format('YYYY-MM-DD')) {
            return { grade_section_id };
          }
        });
        getProgress(studentId, current_grade[0]?.grade_section_id?.grade?.id, current_grade[0]?.grade_section_id?.id); // fetch student progress for all subjects

        getOverallMaerialProgress(id, response?.data?.data?.gradelist[0]?.grade_section_id?.grade?.id); // get student's overall material progress
        // get student's class average
        // getStudentClassAverage(response?.data?.data?.gradelist[0]?.grade_section_id?.id);
        getLeaderboardStatsForStudent(id, response?.data?.data?.gradelist[0]?.grade_section_id?.grade?.id); // get leaderboard stats
      }, (error) => {
        console.log("Error catched: ", error?.response);
        toast.error("Can not fetch user information!");
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };
  // fetches student related assessments
  const getStudentAssessments = (id: number) => {
    getStudentAssessmentData(id).then(
      (response) => {
        console.log("Response for student assessments: ", response?.data?.data);
        // filtering the assessments for stats
        response?.data?.data?.forEach((each) => {
          if (each?.assessment?.category !== "homework") {
            each?.current_status === 'attempted' && setStudentAssessmentStats((oldData) => ({ ...oldData, attempted: oldData.attempted += 1 }));
            each?.current_status === 'progress' && setStudentAssessmentStats((oldData) => ({ ...oldData, progress: oldData.progress += 1 }));
            each?.current_status === 'unattempted' && setStudentAssessmentStats((oldData) => ({ ...oldData, unattempted: oldData.unattempted += 1 }));
          }
          // filtering the homework for stats
          if (each?.assessment?.category === 'homework') {
            each?.current_status === 'attempted' && setStudentHomeworkStats((oldData) => ({ ...oldData, attempted: oldData.attempted += 1 }));
            each?.current_status === 'progress' && setStudentHomeworkStats((oldData) => ({ ...oldData, progress: oldData.progress += 1 }));
            each?.current_status === 'unattempted' && setStudentHomeworkStats((oldData) => ({ ...oldData, unattempted: oldData.unattempted += 1 }));
          }
        });
        // calculate total
        setStudentAssessmentStats((oldData) => ({ ...oldData, total: oldData.attempted + oldData.progress + oldData.unattempted }));
        setStudentHomeworkStats((oldData) => ({ ...oldData, total: oldData.attempted + oldData.progress + oldData.unattempted }));
      }, (error) => {
        console.log("Error caught: ", error?.resoponse);
        toast.error("Could not fetch assessment stats!");
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };

  // *** STATS ***
  // get progress of student
  const getProgress = (userId: number, gradeId: number, gradeSectionId: number) => {
    getProgressForAllTopic(userId, gradeId, gradeSectionId).then(
      (response: AxiosResponse) => {
        // console.log("Response for material progress: ", response?.data);
        let label: string[] = [];
        let local_label: Set<any> = new Set();
        let local_progress: number[] = [];
        let local_performance: number[] = [];

        Object.keys(response?.data).map(e => response?.data[e]).forEach(item => item.forEach(each => {
          each.performance && local_performance.push(Math.round(Number(each.performance)));
          each.progress && local_progress.push(Math.round(Number(each.progress)));
          each.subject_name && local_label.add(each.subject_name)
        }));
        label = Array.from(local_label);
        setStudentMaterialProgress((oldData) => ({ ...oldData, labels: label, progress: local_progress, performance: local_performance }));

      }, (error) => {
        console.log("Error caught: ", error?.response);
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };

  // get student's material progress
  const getOverallMaerialProgress = (userId: number, gradeId: number) => {
    getStudentOverallMaterialProgress(userId, gradeId).then(
      (response: AxiosResponse) => {
        // console.log("Response for overall material progress: ", response?.data?.data);
        setTotalMaterialProgress(response?.data?.data[0]?.progress);
        setActivityData((oldData) => (
          [...oldData, {
            label: 'Progress',
            value: Math.round(Number(response?.data?.data[0]?.progress)) || 0,
            color: "green",
            textColor: 'pink'
          }]
        ));
      }, (error) => {
        console.log("Error caught: ", error?.response);
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };

  // get leaderboard stats
  const getLeaderboardStatsForStudent = (studentId: number, gradeId: number) => {
    getStudentLeaderboardStats(studentId, gradeId).then(
      (response: AxiosResponse) => {
        // console.log("Response for leaderboard: ", response?.data);
        setLeaderboardStats(response?.data);
        setActivityData((oldData) => (
          [...oldData, {
            label: 'Score',
            value: Math.floor((Number(response?.data?.user?.marks_obtained) / Number(response?.data?.user?.total_marks)) * 100 || 0),
            color: '#055DC9',
            textColor: 'green'
          }]
        ));
      }, (error) => {
        console.log("Error caught: ", error?.response);
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };
  
  // ger student's app usage data
  const getStudentAppUsage = (userId: number) => {
    getAppUsagesDataByStudent(userId).then(
      (response: AxiosResponse) => {
        // console.log("Response for student's app usage: ", response?.data?.data);
        setAppUsage(response?.data?.data);
        calculateDaysUsage(response?.data?.data, 7);

      }, (error) => {
        console.log("Error caught: ", error?.response);
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };

  // calculates user app usage based on days(7 days or 30 days)
  const calculateDaysUsage = (usageData, days: number) => {
    let lastSpecificDaysUsage = usageData?.filter((each) => ( // filters out data based on day - days parameter
      each?.usage_on >= moment().subtract(days, 'd').format('YYYY-MM-DD') && each?.usage_on <= moment().format('YYYY-MM-DD')
    ));
    // console.log(`Last ${days} days usage: `, lastSpecificDaysUsage);
    let groupedSpecificDaysUsage = groupBy(lastSpecificDaysUsage, 'usage_on'); // groups usage based on date

    let formatedSevenDaysUsage = Object.keys(groupedSpecificDaysUsage)?.map((each, index) => { // formats data based on date to get total usage for a particular day
      return {
        usageDay: Object.keys(groupedSpecificDaysUsage)[index],
        usage: groupedSpecificDaysUsage[each]?.reduce((accu, { time_in_min }) => accu += time_in_min, 0)
      }
    });

    // setting data to depict line chart
    setAppUsageChartData((oldData) => ({
      ...oldData,
      labels: formatedSevenDaysUsage?.map(({ usageDay }) => { return moment(usageDay).format("MMM Do") }),
      data: formatedSevenDaysUsage?.map(({ usage }) => { return usage }),
    }));
    console.log(`Last ${days} days usage: `, formatedSevenDaysUsage);
  };
  // *** STATS ENDS ***

  // for assessment/homework stat
  const tabDetail = [{
    title: 'Test',
    icon: '',
    tabKey: 1
  },
  {
    title: 'Homework',
    icon: '',
    tabKey: 2
  }];
  // for profile/stat
  const tabForStat = [{
    title: 'Profile',
    icon: '',
    tabKey: 1
  },
  {
    title: 'Stats',
    icon: '',
    tabKey: 2
  }];

  // toggles the options for line chart
  const toggleLineChartNavs = (e, index) => {
    e.preventDefault();
    setActiveNav(index);
    // setChartExample1Data("data" + index);
    index === 1 ? calculateDaysUsage(appUsage, 7) : calculateDaysUsage(appUsage, 30);
  };

  return (
    <React.Fragment>
      {/* <UserHeader /> */}
      <Header />

      {/* Page content */}
      <Container className="mt--7" fluid>
        <Row>
          <Col className="order-xl-1 mb-5 mb-xl-0" xl="4">
            <Card className="card-profile shadow">
              <Row className="justify-content-center">
                <Col className="order-lg-2" lg="3">
                  <div className="card-profile-image mt--4" style={{ position: 'relative' }}>
                    <a onClick={(e) => e.preventDefault()}>
                      <Image source={selectedImg ? selectedImg : fileID !== 0 ? studentInformation?.avatar?.data?.thumbnails && studentInformation?.avatar?.data?.thumbnails[3]?.url : ''} classes={''} />
                    </a>
                  </div>
                </Col>
              </Row>
              <CardHeader className="text-center border-0 pt-8 pt-md-4 pb-0 mb-0 pb-md-4">
              </CardHeader>
              <CardBody className="pt-0 pt-md-4">
                <Row>
                  <div className="col">
                    <div className="card-profile-stats d-flex justify-content-between mt-md-5">
                      <div>
                        <p className="font-weight-bold mb-0">{studentProfile?.gradelist[0]?.grade_section_id?.grade?.name}</p>
                        <span className="description">Grade</span>
                      </div>
                      <div>
                        <p className="font-weight-bold mb-0">{studentProfile?.gradelist[0]?.grade_section_id?.section?.type}</p>
                        <span className="description">Section</span>
                      </div>
                    </div>
                  </div>
                </Row>
                <div className="text-center">
                  <h3 style={{ position: 'relative' }}>
                    {studentInformation?.first_name} {studentInformation?.last_name}
                  </h3>
                  <div>
                    <i className="ni education_hat mr-2" />
                    {studentProfile?.address}
                  </div>
                  <hr className="my-2" />
                </div>
                {/* ASSESSMENT STATS */}
                <div>
                  <OptionTabs tabDetails={tabDetail} setState={setTab} setStateValue={tab} />
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-end">
                        <Badge color="info" className="text-dark">Total: {tab === 1 ? studentAssessmentStats?.total : studentHomeworkStats?.total}</Badge>
                      </div>
                      <p className="small">Attempted: <span className="text-primary">{tab === 1 ? studentAssessmentStats?.attempted : studentHomeworkStats?.attempted}</span></p>
                      <p className="small">Unattempted: <span className="text-primary">{tab === 1 ? studentAssessmentStats?.unattempted : studentHomeworkStats?.unattempted}</span></p>
                    </CardBody>
                  </Card>
                </div>

                <hr className="my-4" />
                {statTab === Number(2) ? <div>
                  <h6 className="heading-small text-muted mb-4">
                    Student Performance
                  </h6>
                  <div style={{ maxHeight: '200px', maxWidth: '200px' }}>
                    <ActivityRing
                      data={activityData}
                      ringWidth={3}
                      hasLabel={true}
                      hasVerticalStats={true}
                      innerLabel={''} />
                  </div></div> : <></>}
              </CardBody>
            </Card>
          </Col>

          <Col className="order-xl-2" xl="8">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0 pb-0">
                <Row className="align-items-center">
                  <Col xs="12">
                    {tabForStat.map((each, index) => (
                      <Button size="sm" className={"mb-0 font-weight-bold capitalize mr-2 pointer "}
                        color={index + 1 === statTab ? 'primary' : ''}
                        key={index}
                        onClick={() => setStatTab(index + 1)}>{each.title}</Button> // active-stat-tab
                    ))}
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* INFORMATION */}
                {statTab === 1 && <Form>
                  <h6 className="heading-small text-muted mb-4">
                    student information
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="small font-weight-bold" // form-control-label
                            htmlFor="input-first-name">
                            First name
                          </label>
                          <Input
                            className="form-control-alternative"
                            // defaultValue={ isEditing ? editableUserInfo?.first_name : studentInformation?.first_name }
                            value={isEditing ? editableUserInfo?.first_name || '' : studentInformation?.first_name || ''}
                            onChange={(e) => setEditableUserInfo((oldData) => ({ ...oldData, first_name: e.target.value }))}
                            id="input-first-name"
                            placeholder="First name"
                            type="text"
                            disabled={!isEditing}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="small font-weight-bold"
                            htmlFor="input-last-name">
                            Last name
                          </label>
                          <Input
                            className="form-control-alternative"
                            value={isEditing ? editableUserInfo?.last_name || '' : studentInformation?.last_name || ''}
                            onChange={(e) => setEditableUserInfo((oldData) => ({ ...oldData, last_name: e.target.value }))}
                            id="input-last-name"
                            placeholder="Last name"
                            type="text"
                            disabled={!isEditing}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="small font-weight-bold"
                            htmlFor="input-username">
                            Contact
                          </label>
                          <Input
                            className="form-control-alternative"
                            value={isEditing ? editableUserInfo?.contact_no || '' : studentProfile?.contact_no || 'Not Available'}
                            onChange={(e) => setEditableUserInfo((oldData) => ({ ...oldData, contact_no: e.target.value }))}
                            id="input-contact"
                            type="text"
                            disabled={!isEditing}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="small font-weight-bold"
                            htmlFor="input-email">
                            SRN Number
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-email"
                            placeholder="jesse@example.com"
                            value={isEditing ? editableUserInfo?.srn_no || '' : studentInformation?.srn_no || 'Not Available'}
                            onChange={(e) => setEditableUserInfo((oldData) => ({ ...oldData, email: e.target.value }))}
                            type="email"
                            disabled={!isEditing}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                  <hr className="my-4" />
                  {/* Address */}
                  <h6 className="heading-small text-muted mb-4">
                    School information
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col md="12">
                        <FormGroup>
                          <label
                            className="small font-weight-bold"
                            htmlFor="input-address">
                            Name
                          </label>
                          <Input
                            className="form-control-alternative"
                            value={studentProfile?.school?.name || 'Not Available'}
                            id="input-address"
                            placeholder="Home Address"
                            type="text"
                            disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="small font-weight-bold"
                            htmlFor="school-block">
                            Block
                          </label>
                          <Input
                            className="form-control-alternative"
                            value={isEditing ? editableUserInfo?.contact_no || '' : studentProfile?.school?.block?.name || 'Not Available'}
                            // onChange={ (e) => setEditableUserInfo((oldData) => ({ ...oldData, contact_no: e.target.value })) }
                            id="input-contact"
                            placeholder="Contact Number"
                            type="text"
                            disabled={!isEditing}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="small font-weight-bold"
                            htmlFor="input-email">
                            Board
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-email"
                            placeholder="jesse@example.com"
                            value={isEditing ? editableUserInfo?.email || '' : studentProfile?.school?.board?.name || 'Not Available'}
                            // onChange={ (e) => setEditableUserInfo((oldData) => ({ ...oldData, email: e.target.value })) }
                            type="text"
                            disabled={!isEditing}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="small font-weight-bold"
                            htmlFor="school-city">
                            City
                          </label>
                          <Input
                            className="form-control-alternative"
                            value={isEditing ? editableUserInfo?.contact_no || '' : studentProfile?.school?.city || 'Not Available'}
                            // onChange={ (e) => setEditableUserInfo((oldData) => ({ ...oldData, contact_no: e.target.value })) }
                            id="school-city"
                            placeholder="Contact Number"
                            type="text"
                            disabled={!isEditing}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="small font-weight-bold"
                            htmlFor="school-medium">
                            Medium
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="school-medium"
                            placeholder="jesse@example.com"
                            value={isEditing ? editableUserInfo?.email || '' : studentProfile?.school?.medium?.type || 'Not Available'}
                            // onChange={ (e) => setEditableUserInfo((oldData) => ({ ...oldData, email: e.target.value })) }
                            type="text"
                            disabled={!isEditing}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Form>}

                {/* STATS */}
                {statTab === 2 && <React.Fragment>
                  <Row>
                    {/* <Col md={5} sm={12}>
                      <h6 className="heading-small text-muted mb-4">
                        Student Performance
                      </h6>
                      <div style={{ maxHeight: '200px', maxWidth: '200px' }}>
                        <ActivityRing
                          data={activityData}
                          ringWidth={3}
                          hasLabel={true}
                          hasVerticalStats={true}
                          innerLabel={''} />
                      </div>
                    </Col> */}
                    <Col md={12} sm={12}>
                      {/* <Row className="align-items-center">
                        <div className="col"> */}
                      {/* <Nav className="justify-content-end" pills>
                            <NavItem>
                              <NavLink
                                className={classnames("py-2 px-3", {
                                  active: activeNav === 1,
                                })}
                                href="#pablo"
                                onClick={(e) => toggleLineChartNavs(e, 1)}
                              >
                                <span className="d-none d-md-block small">Last 7 Days</span>
                                <span className="d-md-none">M</span>
                              </NavLink>
                            </NavItem>
                            <NavItem>
                              <NavLink
                                className={classnames("py-2 px-3", {
                                  active: activeNav === 2,
                                })}
                                data-toggle="tab"
                                href="#pablo"
                                onClick={(e) => toggleLineChartNavs(e, 2)}
                              >
                                <span className="d-none d-md-block small">Last 30 Days</span>
                                <span className="d-md-none">W</span>
                              </NavLink>
                            </NavItem>
                          </Nav> */}
                      <div className="d-flex justify-content-end">
                        <Button
                          size="sm"
                          color={activeNav === 1 ? 'primary' : 'secondary'}
                          onClick={(e) => toggleLineChartNavs(e, 1)}>Last 7 Days</Button>
                        <Button
                          size="sm"
                          color={activeNav === 2 ? 'primary' : 'secondary'}
                          onClick={(e) => toggleLineChartNavs(e, 2)}>Last 30 Days</Button>
                      </div>
                      {/* </div>
                      </Row> */}
                      <h6 className="heading-small text-muted mb-4">
                        Student App Usage
                      </h6>
                      {/* LINE CHART - APP USAGES */}
                      <AreaChart
                        chartData={appUsageChartData?.data}
                        chartLabels={appUsageChartData?.labels}
                        title={"Usage In Minutes"}
                        name={"Minutes"} />
                    </Col>
                  </Row>
                  <div className="mt-4">
                    <h6 className="heading-small text-muted mb-4">
                      Subject Wise Progress
                    </h6>
                    {/* <Column 
                      chartData={columnData.chartData} 
                      chartLabels={columnData.chartLabels} 
                      colors={columnData.color} 
                      name={"Percentage(%)"}
                      legend={true} /> */}
                    {studentMaterialProgress?.labels?.length !== 0 ? (
                      <PairedColumnChart
                        firstSeriec={{
                          name: 'Course Progress',
                          data: studentMaterialProgress?.progress
                        }}
                        secondSeries={{
                          name: 'Assessment Performance',
                          data: studentMaterialProgress?.performance
                        }}
                        categories={studentMaterialProgress?.labels}
                      />
                    ) : <p className="text-muted font-weight-bold">No data available to show</p>}
                  </div>
                </React.Fragment>}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default StudentProfile;
